import { Badge, Button, Col, Container, Image, Row, Spinner } from 'react-bootstrap';
import { Link } from '@/compat/react-router-dom';
import { FaCheckCircle } from 'react-icons/fa';
import { useAboutPage, getAboutSectionByKey } from '@/hooks/useAboutPage';
import { getAboutImage } from '@/utils/imageUrl';

const JourneyHero = ({ sectionData: propSectionData }) => {
  const { sections, loading } = useAboutPage();
  const journeyHeroSection = propSectionData || getAboutSectionByKey(sections, 'journey_hero');
  
  const bgColor = journeyHeroSection?.background_color || 'light';
  const bgEffect = journeyHeroSection?.background_effect || null;
  let bgClass = bgColor === 'primary' ? 'section-bg-primary' : bgColor === 'white' ? 'bg-white' : 'bg-light';
  // Apply gradient effect if selected
  if (bgColor === 'primary' && bgEffect === 'gradient') {
    bgClass = 'section-bg-primary-gradient';
  }
  const hasDotsEffect = bgColor === 'primary' && bgEffect === 'dots';
  
  if (loading && !propSectionData) {
    return (
      <section className={`journey-hero position-relative overflow-hidden py-5 py-lg-6 ${bgClass} about-section journey-hero-section`}>
        <Container>
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
          </div>
        </Container>
      </section>
    );
  }
  
  if (!journeyHeroSection) {
    return null;
  }
  
  const featureItems = journeyHeroSection.items || [];
  const firstColumn = featureItems.slice(0, 3);
  const secondColumn = featureItems.slice(3);
  
  const mainImage = journeyHeroSection.content?.main_image || 'pictures/about/journey-hero.jpg';
  const experienceYears = journeyHeroSection.content?.experience_years || '40+';
  const experienceLabel = journeyHeroSection.content?.experience_label || 'Years Experience';
  const quote = journeyHeroSection.content?.quote || '';
  const ctaText = journeyHeroSection.content?.cta_text || 'Discover Our Services';
  const ctaLink = journeyHeroSection.content?.cta_link || '/services';
  const signature = journeyHeroSection.content?.signature || 'Trusted by leading tour operators throughout Europe';
  
  const imageUrl = getAboutImage(mainImage);

  return (
    <section className={`journey-hero position-relative overflow-hidden py-5 py-lg-6 ${bgClass} about-section journey-hero-section`}>
      {hasDotsEffect && (
        <>
          <span className="journey-hero__dots journey-hero__dots--left d-none d-xl-block" />
          <span className="journey-hero__dots journey-hero__dots--image d-none d-lg-block" />
        </>
      )}
      <Container className="position-relative">
        <Row className="g-4 align-items-center flex-lg-row-reverse">
          {imageUrl && (
            <Col lg={5} className="journey-hero__visual-col ps-lg-0">
              <div className="journey-hero__visual pe-xl-4">
                <div className="journey-hero__image shadow-lg overflow-hidden rounded-4 position-relative">
                  <Image
                    src={imageUrl}
                    alt="South Crete Tours team"
                    width={568}
                    height={700}
                    loading="lazy"
                    className="journey-hero__image-main w-100 h-100 object-fit-cover"
                  />
                </div>
                <div className="journey-hero__experience-card shadow-lg">
                  <h2 className="journey-hero__experience-value">{experienceYears}</h2>
                  <p className="journey-hero__experience-label mb-0 text-uppercase">{experienceLabel}</p>
                </div>
              </div>
            </Col>
          )}

          <Col lg={imageUrl ? 7 : 12} className="journey-hero__content-col ps-lg-4 pe-lg-5">
            <div className="journey-hero__content">
              {journeyHeroSection.subtitle && (
                <Badge
                  bg="primary"
                  className="bg-opacity-10 text-primary journey-hero__badge mb-3"
                  data-aos="fade-up"
                  data-aos-duration="800"
                >
                  {journeyHeroSection.subtitle}
                </Badge>
              )}
              {journeyHeroSection.title && (
                <h1
                  className="sec-title mb-3"
                  data-aos="fade-up"
                  data-aos-duration="800"
                  data-aos-delay="100"
                >
                  {journeyHeroSection.title}
                </h1>
              )}
              {journeyHeroSection.intro_text && (
                <p
                  className="lead intro-text mb-0"
                  data-aos="fade-up"
                  data-aos-duration="800"
                  data-aos-delay="140"
                  dangerouslySetInnerHTML={{ __html: journeyHeroSection.intro_text }}
                />
              )}

              {quote && (
                <div className="journey-hero__quote" data-aos="fade-up" data-aos-duration="800" data-aos-delay="180">
                  <h5 className="mb-0 fw-normal journey-hero__quote-text">
                    {quote}
                  </h5>
                </div>
              )}

              {featureItems.length > 0 && (
                <div className="journey-hero__feature-grid" data-aos="fade-up" data-aos-duration="800" data-aos-delay="220">
                  <ul>
                    {firstColumn.map((item) => (
                      <li key={item.id}>
                        <span className="journey-hero__feature-icon" aria-hidden>
                          <FaCheckCircle />
                        </span>
                        {item.title}
                      </li>
                    ))}
                  </ul>
                  <ul>
                    {secondColumn.map((item) => (
                      <li key={item.id}>
                        <span className="journey-hero__feature-icon" aria-hidden>
                          <FaCheckCircle />
                        </span>
                        {item.title}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="journey-hero__cta-group" data-aos="fade-up" data-aos-duration="800" data-aos-delay="260">
                <Button as={Link} to={ctaLink} variant="primary" size="lg">
                  {ctaText}
                </Button>
                {signature && (
                  <div className="journey-hero__signature">
                    <span>{signature}</span>
                  </div>
                )}
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </section>
  );
};

export default JourneyHero;

